import { useCallback, useEffect, useRef, useState } from 'react';
import { Plus } from 'lucide-react';
import apiService from '../services/api';
import CommunicationQuotaBar from '../components/communication/CommunicationQuotaBar';
import CommunicationStatsPanel from '../components/communication/CommunicationStatsPanel';
import CommunicationOffersModal from '../components/communication/CommunicationOffersModal';
import CampaignHistoryTable from '../components/communication/CampaignHistoryTable';
import CampaignFormModal, { emptyForm, applyInitialCampaign } from '../components/communication/CampaignFormModal';
import SendConfirmModal from '../components/communication/SendConfirmModal';
import CampaignDetailModal from '../components/communication/CampaignDetailModal';
import CampaignEditInAppDisplayModal from '../components/communication/CampaignEditInAppDisplayModal';
import { mapPlatformNotificationToHistoryRow } from '../components/communication/channelConfig';
import { sanitizeStorefrontObjectPosition } from '../components/communication/storefrontObjectPosition';

function getCurrentUser() {
  try {
    const userData = localStorage.getItem('admin_user');
    if (userData) return JSON.parse(userData);
  } catch {
    /* ignore */
  }
  return null;
}

function newIdempotencyKey() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `growth-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * Page métier Communication (admin) — sans wizard.
 * - Commerce : historique GET /api/growth/campaigns
 * - SUPER_ADMIN/ADMIN (@ /notifications) : mode Plateforme Clickorico (In-App global)
 */
const Notifications = ({ commercantId: propCommercantId = null, commerceName = null }) => {
  const [currentUser] = useState(() => getCurrentUser());
  const role = currentUser?.role;
  const isStaffPlatform = role === 'SUPER_ADMIN' || role === 'ADMIN';
  const userCommercantId = currentUser?.commercantId || currentUser?.commercant?.id || null;
  const activeCommercantId = propCommercantId || userCommercantId;
  const platformMode = Boolean(isStaffPlatform && !activeCommercantId);

  const resolvedCommerceName = platformMode
    ? 'Clickorico'
    : commerceName ||
      currentUser?.commercant?.nom ||
      currentUser?.commercantName ||
      '';

  const [entitlements, setEntitlements] = useState(null);
  const [marketingEnabled, setMarketingEnabled] = useState(false);
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 20, total: 0, totalPages: 1 });
  const [filters, setFilters] = useState({ q: '', status: '', canal: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [submitting, setSubmitting] = useState(false);
  const [testing, setTesting] = useState(false);
  const [formError, setFormError] = useState('');
  const sendLockRef = useRef(false);
  const sendIdempotencyRef = useRef(null);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailCampaign, setDetailCampaign] = useState(null);
  const [detailStats, setDetailStats] = useState(null);
  const [editInAppOpen, setEditInAppOpen] = useState(false);
  const [editInAppLoading, setEditInAppLoading] = useState(false);
  const [editInAppSaving, setEditInAppSaving] = useState(false);
  const [editInAppCampaign, setEditInAppCampaign] = useState(null);
  const [editInAppError, setEditInAppError] = useState('');
  const [growthStats, setGrowthStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [showOffers, setShowOffers] = useState(false);

  const loadEntitlements = useCallback(async () => {
    if (platformMode || !activeCommercantId) {
      setEntitlements(null);
      setMarketingEnabled(false);
      return;
    }
    try {
      const data = await apiService.getGrowthEntitlements(activeCommercantId);
      setEntitlements(data);
      setMarketingEnabled(Boolean(data?.marketingEnabled));
    } catch (err) {
      console.error('[Communication] entitlements', err);
    }
  }, [activeCommercantId, platformMode]);

  const loadGrowthStats = useCallback(async () => {
    if (platformMode || !activeCommercantId) {
      setGrowthStats(null);
      return;
    }
    try {
      setStatsLoading(true);
      const data = await apiService.getGrowthStats(activeCommercantId);
      setGrowthStats(data);
    } catch (err) {
      console.error('[Communication] stats', err);
      setGrowthStats(null);
    } finally {
      setStatsLoading(false);
    }
  }, [activeCommercantId, platformMode]);

  const loadCampaigns = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      setError('');

      if (platformMode) {
        const data = await apiService.getNotifications();
        let rows = (Array.isArray(data) ? data : [])
          .filter((n) => n.commercantId == null)
          .map(mapPlatformNotificationToHistoryRow);

        if (filters.status) {
          rows = rows.filter((r) => String(r.status).toUpperCase() === filters.status.toUpperCase());
        }
        if (filters.canal) {
          rows = rows.filter((r) => (r.channels || []).includes(filters.canal.toUpperCase()));
        }
        if (filters.q?.trim()) {
          const q = filters.q.trim().toLowerCase();
          rows = rows.filter(
            (r) =>
              String(r.title || '').toLowerCase().includes(q) ||
              String(r.textContent || '').toLowerCase().includes(q)
          );
        }

        rows.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        const pageSize = 20;
        const total = rows.length;
        const totalPages = Math.max(1, Math.ceil(total / pageSize));
        const currentPage = Math.min(Math.max(1, page), totalPages);
        const slice = rows.slice((currentPage - 1) * pageSize, currentPage * pageSize);
        setItems(slice);
        setPagination({ page: currentPage, pageSize, total, totalPages });
        return;
      }

      if (!activeCommercantId) {
        setItems([]);
        return;
      }

      const data = await apiService.getGrowthCampaigns({
        commercantId: activeCommercantId,
        status: filters.status || undefined,
        canal: filters.canal || undefined,
        q: filters.q || undefined,
        page,
        pageSize: pagination.pageSize || 20,
      });
      setItems(data?.items || []);
      setPagination(data?.pagination || { page: 1, pageSize: 20, total: 0, totalPages: 1 });
    } catch (err) {
      console.error('[Communication] history', err);
      setError(err?.message || 'Impossible de charger l’historique');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [
    platformMode,
    activeCommercantId,
    filters.status,
    filters.canal,
    filters.q,
    pagination.pageSize,
  ]);

  useEffect(() => {
    loadEntitlements();
    loadGrowthStats();
  }, [loadEntitlements, loadGrowthStats]);

  useEffect(() => {
    const t = setTimeout(() => loadCampaigns(1), filters.q ? 300 : 0);
    return () => clearTimeout(t);
  }, [loadCampaigns, filters.q, filters.status, filters.canal]);

  const openCreate = (initialCampaign) => {
    setForm(applyInitialCampaign(initialCampaign));
    setFormError('');
    setShowForm(true);
  };

  const buildCampaignMeta = () => {
    const meta = {};
    // Snapshot autonome In-App : toujours poser la clé (URL ou null explicite).
    if ((form.channels || []).includes('IN_APP')) {
      meta.imageUrl = form.imageUrl || null;
    } else if (form.imageUrl) {
      meta.imageUrl = form.imageUrl;
    }
    if (form.ctaLabel?.trim() && form.ctaUrl?.trim()) {
      meta.ctaLabel = form.ctaLabel.trim();
      meta.ctaUrl = form.ctaUrl.trim();
    } else if (form.ctaUrl?.trim()) {
      meta.ctaUrl = form.ctaUrl.trim();
    }
    if (form.sourceOpportunity) meta.sourceOpportunity = form.sourceOpportunity;
    if ((form.channels || []).includes('IN_APP') && form.imageUrl) {
      meta.storefrontObjectPosition = sanitizeStorefrontObjectPosition(
        form.storefrontObjectPosition
      );
    }
    return Object.keys(meta).length ? meta : undefined;
  };

  const inAppPayloadExtras = () => {
    const extras = {};
    if (form.imageUrl) extras.imageUrl = form.imageUrl;
    if (form.ctaLabel?.trim() && form.ctaUrl?.trim()) {
      extras.ctaLabel = form.ctaLabel.trim();
      extras.ctaUrl = form.ctaUrl.trim();
    } else if (form.ctaUrl?.trim()) {
      extras.ctaUrl = form.ctaUrl.trim();
    }
    return extras;
  };

  const buildStorefrontPayload = () => {
    if (platformMode || !(form.channels || []).includes('IN_APP')) {
      return { storefrontEnabled: false };
    }
    if (!form.storefrontEnabled) {
      return { storefrontEnabled: false };
    }
    const payload = {
      storefrontEnabled: true,
      storefrontDurationPreset: form.storefrontDurationPreset || '14',
    };
    if (form.storefrontDurationPreset === 'custom') {
      payload.storefrontCustomDays = form.storefrontCustomDays ?? 14;
    }
    return payload;
  };

  const handleSendTest = async () => {
    if (testing || platformMode) return;
    try {
      setTesting(true);
      setFormError('');
      if (!marketingEnabled) {
        throw new Error('Email marketing désactivé — envoi test impossible.');
      }
      await apiService.sendGrowthCampaignTest({
        commercantId: activeCommercantId,
        toEmail: form.testEmail,
        title: form.title || form.subject || 'Test',
        subject: form.subject || form.title,
        message: form.message,
        textContent: form.message,
        ...inAppPayloadExtras(),
        meta: buildCampaignMeta(),
      });
      setSuccess('Email de test envoyé (hors quota).');
    } catch (err) {
      setFormError(err?.message || 'Échec de l’envoi test');
    } finally {
      setTesting(false);
    }
  };

  const executeSend = async () => {
    if (sendLockRef.current || submitting) return;
    sendLockRef.current = true;

    try {
      setSubmitting(true);
      setFormError('');
      setError('');

      if (platformMode) {
        if (!form.title.trim() || !form.message.trim()) {
          throw new Error('Titre et message requis.');
        }
        await apiService.createNotification({
          canal: 'IN_APP',
          titre: form.title,
          message: form.message,
          type: 'GENERAL',
          destinatairesType: 'ALL',
          ...inAppPayloadExtras(),
          // Pas de commercantId → communication Plateforme Clickorico
        });
        setShowConfirm(false);
        setShowForm(false);
        setForm(emptyForm());
        sendIdempotencyRef.current = null;
        setSuccess('Communication plateforme envoyée (In-App).');
        await loadCampaigns(1);
        return;
      }

      const channels = (form.channels || []).filter((c) => {
        if (c === 'EMAIL' && !marketingEnabled) return false;
        if (c === 'PUSH') return false;
        return true;
      });
      const hasEmail = channels.includes('EMAIL');
      const hasInApp = channels.includes('IN_APP');

      if (hasEmail && !marketingEnabled) {
        throw new Error('Email marketing désactivé. Décochez Email ou réessayez plus tard.');
      }
      if (!hasEmail && !hasInApp) {
        throw new Error('Sélectionnez au moins un canal.');
      }
      if (!activeCommercantId && currentUser?.role === 'COMMERCANT') {
        throw new Error('Commerce non associé.');
      }

      if (!sendIdempotencyRef.current) {
        sendIdempotencyRef.current = newIdempotencyKey();
      }
      const sendIdempotencyKey = sendIdempotencyRef.current;

      let growthCampaignId = null;
      let emailError = null;
      let emailResult = null;

      if (hasEmail) {
        try {
          const created = await apiService.createGrowthCampaign({
            commercantId: activeCommercantId,
            title: form.title || form.subject || 'Campagne',
            subject: form.subject || form.title,
            message: form.message,
            textContent: form.message,
            channels,
            kind: 'DRAFT',
            meta: buildCampaignMeta(),
            ...buildStorefrontPayload(),
          });
          growthCampaignId = created?.campaign?.id;
          if (!growthCampaignId) throw new Error('Création de campagne impossible');

          emailResult = await apiService.sendGrowthCampaign(growthCampaignId, {
            commercantId: activeCommercantId,
            sendIdempotencyKey,
          });
        } catch (err) {
          emailError = err;
          if (!hasInApp) throw err;
        }
      }

      if (hasInApp) {
        await apiService.createNotification({
          canal: 'IN_APP',
          titre: form.title || form.subject || 'Message',
          message: form.message,
          type: 'GENERAL',
          destinatairesType: 'ALL',
          commercantId: activeCommercantId,
          ...inAppPayloadExtras(),
          ...buildStorefrontPayload(),
          ...(growthCampaignId
            ? {
                skipGrowthQuota: true,
                linkedGrowthCampaignId: growthCampaignId,
              }
            : {}),
        });
      }

      setShowConfirm(false);
      setShowForm(false);
      setForm(emptyForm());
      sendIdempotencyRef.current = null;

      if (emailError) {
        setSuccess(
          hasInApp
            ? `In-App envoyé. Email en échec : ${emailError.message || 'erreur'}. Quota : 1 unité max.`
            : null
        );
        setFormError(emailError.message || 'Erreur email');
      } else if (emailResult?.campaign?.status === 'FAILED' || emailResult?.summary?.status === 'FAILED') {
        setSuccess(
          hasInApp
            ? 'In-App envoyé. L’email n’a pas abouti (aucune 2ᵉ consommation de quota).'
            : 'Campagne email en échec.'
        );
      } else {
        setSuccess('Campagne envoyée.');
      }
      await Promise.all([loadCampaigns(1), loadEntitlements(), loadGrowthStats()]);
    } catch (err) {
      console.error('[Communication] send', err);
      setFormError(err?.message || 'Erreur lors de l’envoi');
      setShowConfirm(false);
    } finally {
      setSubmitting(false);
      sendLockRef.current = false;
    }
  };

  const handleView = async (row) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setDetailCampaign(null);
    setDetailStats(null);
    try {
      if (platformMode || row.source === 'platform') {
        setDetailCampaign(row);
        return;
      }
      const data = await apiService.getGrowthCampaign(row.id, activeCommercantId);
      setDetailCampaign(data?.campaign || row);
      setDetailStats(data?.stats || row.stats || null);
    } catch (err) {
      setDetailCampaign(null);
      setDetailStats(null);
      setError(err?.message || 'Détail indisponible');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleEditInAppDisplay = async (row) => {
    if (platformMode || !activeCommercantId) return;
    setEditInAppOpen(true);
    setEditInAppLoading(true);
    setEditInAppError('');
    setEditInAppCampaign(null);
    try {
      const data = await apiService.getGrowthCampaign(row.id, activeCommercantId);
      setEditInAppCampaign(data?.campaign || row);
    } catch (err) {
      setEditInAppError(err?.message || 'Impossible de charger la campagne');
      setEditInAppOpen(false);
    } finally {
      setEditInAppLoading(false);
    }
  };

  const handleSaveInAppDisplay = async (payload) => {
    if (!editInAppCampaign?.id || editInAppSaving) return;
    try {
      setEditInAppSaving(true);
      setEditInAppError('');
      await apiService.patchGrowthCampaignInAppDisplay(editInAppCampaign.id, {
        commercantId: activeCommercantId,
        ...payload,
      });
      setEditInAppOpen(false);
      setEditInAppCampaign(null);
      setSuccess('Affichage In-App mis à jour');
      await loadCampaigns(pagination.page || 1);
    } catch (err) {
      setEditInAppError(err?.message || 'Échec de la mise à jour');
    } finally {
      setEditInAppSaving(false);
    }
  };

  const openConfirm = () => {
    setFormError('');
    sendIdempotencyRef.current = newIdempotencyKey();
    setShowConfirm(true);
  };

  if (!platformMode && !activeCommercantId && role === 'COMMERCANT') {
    return (
      <div className="p-6">
        <div className="card text-sm text-amber-800 bg-amber-50 border border-amber-100">
          Aucun commerce associé à ce compte.
        </div>
      </div>
    );
  }

  if (!platformMode && !activeCommercantId) {
    return (
      <div className="p-6">
        <div className="card text-sm text-gray-600">
          Ouvrez la fiche d’un commerçant pour gérer sa Communication, ou connectez-vous en tant que
          commerçant.
        </div>
      </div>
    );
  }

  const maxRecipients = entitlements?.caps?.maxEmailRecipientsPerCampaign;
  const historyTitle = platformMode
    ? 'Historique plateforme'
    : 'Historique des campagnes';

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">
            {platformMode ? 'Communication Clickorico' : 'Communication'}
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            {platformMode
              ? 'Messages plateforme In-App destinés à tous les clients — hors campagnes des commerces.'
              : 'Informez et fidélisez vos clients — In-App et email marketing (hors emails de commande).'}
          </p>
        </div>
        <button type="button" className="btn-primary inline-flex items-center" onClick={openCreate}>
          <Plus className="h-4 w-4 mr-2" />
          {platformMode ? 'Créer une communication' : 'Créer une campagne'}
        </button>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 border border-red-100 text-red-700 text-sm px-4 py-3">
          {error}
        </div>
      )}
      {success && (
        <div className="rounded-md bg-emerald-50 border border-emerald-100 text-emerald-800 text-sm px-4 py-3 flex justify-between gap-3">
          <span>{success}</span>
          <button type="button" className="text-emerald-700 underline" onClick={() => setSuccess('')}>
            OK
          </button>
        </div>
      )}

      <CommunicationQuotaBar
        entitlements={entitlements}
        marketingEnabled={marketingEnabled}
        platformMode={platformMode}
        onDiscoverOffers={() => setShowOffers(true)}
      />

      {!platformMode && (
        <CommunicationStatsPanel stats={growthStats} loading={statsLoading} />
      )}

      <div>
        <h2 className="text-lg font-medium text-gray-900 mb-3">{historyTitle}</h2>
        <CampaignHistoryTable
          items={items}
          loading={loading}
          filters={filters}
          onFiltersChange={setFilters}
          onView={handleView}
          onEditInAppDisplay={handleEditInAppDisplay}
          pagination={pagination}
          onPageChange={(page) => loadCampaigns(page)}
        />
      </div>

      <CampaignFormModal
        isOpen={showForm}
        onClose={() => !submitting && setShowForm(false)}
        form={form}
        onChange={setForm}
        marketingEnabled={marketingEnabled}
        commerceName={resolvedCommerceName}
        entitlements={entitlements}
        platformMode={platformMode}
        onRequestSend={openConfirm}
        onSendTest={handleSendTest}
        submitting={submitting}
        testing={testing}
        error={formError}
      />

      <SendConfirmModal
        isOpen={showConfirm}
        onClose={() => !submitting && setShowConfirm(false)}
        onConfirm={executeSend}
        form={form}
        submitting={submitting}
        platformMode={platformMode}
        maxRecipients={platformMode ? undefined : maxRecipients}
        recipientEstimate={null}
        audienceHint={
          platformMode
            ? 'tous les clients actifs Clickorico (calcul serveur)'
            : form.channels?.includes('EMAIL')
              ? 'nombre exact calculé à l’envoi (opt-in email / clients In-App)'
              : 'clients ayant commandé chez ce commerce (calcul serveur)'
        }
      />

      <CampaignDetailModal
        isOpen={detailOpen}
        onClose={() => setDetailOpen(false)}
        campaign={detailCampaign}
        stats={detailStats}
        loading={detailLoading}
      />

      <CampaignEditInAppDisplayModal
        isOpen={editInAppOpen && !editInAppLoading}
        onClose={() => !editInAppSaving && setEditInAppOpen(false)}
        campaign={editInAppCampaign}
        commerceName={resolvedCommerceName}
        onSave={handleSaveInAppDisplay}
        saving={editInAppSaving}
        error={editInAppError}
      />

      <CommunicationOffersModal
        isOpen={showOffers}
        onClose={() => setShowOffers(false)}
        currentTier={entitlements?.tier || 'INCLUS'}
      />
    </div>
  );
};

export default Notifications;
